import type {ComponentProps, CSSProperties} from 'react'
import {Toaster as SonnerToaster} from 'sonner'
import {cn} from '@/lib/utils'

export type ToasterProps = ComponentProps<typeof SonnerToaster>

const TOAST_STYLE = {
	'--normal-bg': 'var(--card)',
	'--normal-text': 'var(--foreground)',
	'--normal-border': 'var(--border)',
} as CSSProperties

const TOAST_CLASS = cn(
	'group relative flex w-full items-start gap-3 overflow-hidden rounded-[var(--radius)] border border-border bg-card py-4 pl-5 pr-9 text-card-foreground',
	"before:absolute before:top-0 before:bottom-0 before:left-0 before:w-[3px] before:bg-border before:content-['']",
	'data-[type=success]:before:bg-success',
	'data-[type=error]:before:bg-destructive',
	'data-[type=warning]:before:bg-warning',
	'data-[type=info]:before:bg-info',
)

const BASE_CLASSES = {
	toast: TOAST_CLASS,
	title: 'text-sm font-medium leading-snug text-foreground',
	description: 'text-[13px] leading-snug text-muted-foreground',
	actionButton:
		'inline-flex h-7 items-center rounded-[10px] border border-border bg-transparent px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-muted',
	cancelButton:
		'inline-flex h-7 items-center rounded-[10px] bg-transparent px-2.5 text-xs text-muted-foreground transition-colors hover:text-foreground',
	// Sonner ships its own close-button styles that win the cascade unless
	// we use !important — keep these prefixes when adjusting. The position
	// overrides exist because sonner 2.0.7 hardcodes the button half-outside
	// the top-LEFT corner (top: 0; translate(-35%,-35%)), where our
	// overflow-hidden clips it against the left accent bar — re-verify on any
	// sonner bump.
	closeButton:
		'!left-auto !right-1 !top-1 !transform-none !border-0 !bg-transparent !text-muted-foreground hover:!text-foreground',
	icon: 'hidden',
} as const

type ClassNames = NonNullable<ToasterProps['toastOptions']>['classNames']

function mergeClassNames(overrides: ClassNames | undefined): ClassNames {
	if (!overrides) return BASE_CLASSES
	return {
		toast: cn(BASE_CLASSES.toast, overrides.toast),
		title: cn(BASE_CLASSES.title, overrides.title),
		description: cn(BASE_CLASSES.description, overrides.description),
		actionButton: cn(BASE_CLASSES.actionButton, overrides.actionButton),
		cancelButton: cn(BASE_CLASSES.cancelButton, overrides.cancelButton),
		closeButton: cn(BASE_CLASSES.closeButton, overrides.closeButton),
		icon: cn(BASE_CLASSES.icon, overrides.icon),
	}
}

export function Toaster({
	theme = 'light',
	visibleToasts = 3,
	offset = 16,
	gap = 8,
	style,
	toastOptions,
	...rest
}: ToasterProps) {
	return (
		<SonnerToaster
			theme={theme}
			visibleToasts={visibleToasts}
			offset={offset}
			gap={gap}
			style={{...TOAST_STYLE, ...style}}
			toastOptions={{
				...toastOptions,
				classNames: mergeClassNames(toastOptions?.classNames),
			}}
			{...rest}
		/>
	)
}
